import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { environment } from '../../../../../../environments/environment';
import { PermitDetail } from '../../../models/permit-detail.model';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

/**
 * Guest magic-link permit view. The GET returns only a locked stub (type +
 * status, no personal details); the applicant unlocks the full detail by
 * confirming the email on their application (POST .../verify). This protects
 * the personal details behind the link if it is ever forwarded or leaked.
 *
 * Once confirmed, the server issues a short-lived grant which we keep on this
 * device so a refresh doesn't mean retyping the address. We store the GRANT, never
 * the email - it expires server-side, and it's useless for any other permit.
 */
@Component({
    selector: 'app-view-permit',
    templateUrl: './view-permit.component.html',
    styleUrls: ['./view-permit.component.scss'],
    standalone: false,
})
export class ViewPermitComponent implements OnInit {
    public permit?: PermitDetail | null;
    public loading = true;
    public notFound = false;
    public token?: string;

    // Email-confirm gate
    public locked = true;
    public permitType?: string;
    public status?: string;
    public email = '';
    public verifying = false;
    public verifyError?: string;
    /** True when this device is unlocked by a remembered grant rather than a fresh entry. */
    public remembered = false;

    /** True from the moment Stripe confirms until the webhook issues the permit. */
    public awaitingIssue = false;

    /** Whether this device can also list the applicant's other permits. */
    public get canListAll(): boolean {
        return !this.locked && !!this.grants.readAccount();
    }

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private route: ActivatedRoute,
        private grants: PermitViewGrantService,
    ) {}

    public get pdfUrl(): string | null {
        return this.token ? `${environment.apiUrl}/api/public/permit/${this.token}/pdf` : null;
    }

    ngOnInit(): void {
        this.token = this.route.snapshot.paramMap.get('token') || undefined;

        const grant = this.token ? this.grants.read(this.token) : null;
        if (grant) {
            this.resume(grant);
            return;
        }
        this.loadStub();
    }

    /** Locked stub - permit type and status only, no personal details. */
    private loadStub(): void {
        this.api
            .get({
                url: `/api/public/permit/${this.token}`,
                skipAuth: true,
                bypassOfflineQueue: true,
                useCache: false,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<PermitDetail>) => {
                    this.loading = false;
                    this.permitType = res.data?.permitType;
                    this.status = res.data?.status;
                    this.locked = res.data?.locked !== false;
                },
                (_err: ApiErrorResponse<any>) => {
                    this.loading = false;
                    this.notFound = true;
                },
            );
    }

    /** Skip the gate using the grant from a previous confirmation on this device. */
    private resume(grant: string): void {
        this.api
            .post({
                url: `/api/public/permit/${this.token}/resume`,
                data: { grant },
                skipAuth: true,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<PermitDetail>) => {
                    this.loading = false;
                    this.permit = res.data;
                    this.locked = false;
                    this.remembered = true;
                },
                (_err: ApiErrorResponse<any>) => {
                    // Expired or no longer accepted - drop it and fall back to the gate.
                    this.clearGrant();
                    this.loadStub();
                },
            );
    }

    public verify(): void {
        if (!this.email || this.verifying) {
            return;
        }
        this.verifying = true;
        this.verifyError = undefined;
        this.api
            .post({
                url: `/api/public/permit/${this.token}/verify`,
                data: { email: this.email },
                skipAuth: true,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<PermitDetail>) => {
                    this.verifying = false;
                    this.permit = res.data;
                    this.locked = false;
                    this.email = '';
                    this.storeGrant(res.data);
                },
                (err: ApiErrorResponse<any>) => {
                    this.verifying = false;
                    this.verifyError =
                        err?.error?.status === 403
                            ? "That email doesn't match this permit. Please use the email address on your application."
                            : 'Sorry, something went wrong. Please try again.';
                },
            );
    }

    /**
     * Payment succeeded. The permit is issued by the Stripe webhook, which lands a
     * moment later and out of band - so poll rather than assume, and keep the
     * "processing" message up until the status actually moves off pending_payment.
     */
    public onPaid(): void {
        this.awaitingIssue = true;
        this.pollForIssue(0);
    }

    private pollForIssue(attempt: number): void {
        const grant = this.token ? this.grants.read(this.token) : null;
        if (!grant || attempt > 10) {
            // Give up waiting and just show whatever the permit says now.
            this.awaitingIssue = false;
            if (grant) this.resume(grant);
            return;
        }

        setTimeout(() => {
            this.api
                .post({
                    url: `/api/public/permit/${this.token}/resume`,
                    data: { grant },
                    skipAuth: true,
                    displayErrors: false,
                })
                .subscribe(
                    (res: ApiResponse<PermitDetail>) => {
                        this.permit = res.data;
                        if (res.data?.status === 'pending_payment') {
                            this.pollForIssue(attempt + 1);
                            return;
                        }
                        this.awaitingIssue = false;
                    },
                    (_err: ApiErrorResponse<any>) => this.pollForIssue(attempt + 1),
                );
        }, 1500);
    }

    /** Re-lock this device - for shared or borrowed computers. */
    public forgetDevice(): void {
        this.clearGrant();
        this.permit = null;
        this.locked = true;
        this.remembered = false;
        this.loading = true;
        this.loadStub();
    }

    private storeGrant(detail?: PermitDetail | null): void {
        this.grants.remember({ token: this.token, ...detail });
    }

    private clearGrant(): void {
        if (this.token) this.grants.clear(this.token);
        // The account grant is the same proof, so re-locking must drop it too -
        // otherwise "forget me" would leave the full list reachable.
        this.grants.clearAccount();
    }
}
